import Logo from './Logo';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import React, { useState, useEffect } from 'react';
import { useClient } from '../contexts/ClientContext';
import { LockIcon, XIcon } from './Icons';

const ClientAuthModal: React.FC = () => {
    const { isAuthModalOpen, closeAuthModal, loginClient, registerClient, authPrefill } = useClient();
    useBodyScrollLock(isAuthModalOpen);
    const [mode, setMode] = useState<'login' | 'register'>('login');
    
    // Form States
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [birthDate, setBirthDate] = useState('');
    const [phone, setPhone] = useState('');
    const [city, setCity] = useState('');
    const [gender, setGender] = useState<'homme' | 'femme'>('femme');
    const [password, setPassword] = useState('');
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [error, setError] = useState('');

    // PRE-FILL EFFECT
    useEffect(() => {
        if (isAuthModalOpen && authPrefill) {
            setMode('register');
            if (authPrefill.name) setName(authPrefill.name);
            if (authPrefill.email) setEmail(authPrefill.email);
            if (authPrefill.phone) setPhone(authPrefill.phone);
            if (authPrefill.address) setCity(authPrefill.address);
            if (authPrefill.gender) setGender(authPrefill.gender);
            if (authPrefill.birthDate) setBirthDate(authPrefill.birthDate);
        }
    }, [isAuthModalOpen, authPrefill]);

    if (!isAuthModalOpen) return null;

    const validatePhone = (p: string) => {
        const phoneRegex = /^(?:(?:\+|00)33|0)\s*[1-79](?:[\s.-]*\d{2}){4}$/;
        return phoneRegex.test(p);
    };

    const validateEmail = (e: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(e);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (mode === 'login') {
            const res = loginClient(email, password);
            if (res && res.success) {
                closeAuthModal();
            } else {
                setError(res?.message || "Email ou mot de passe incorrect.");
            }
        } else {
            if (!name.trim() || name.length < 2) {
                setError("Prénom trop court.");
                return;
            }
            if (!birthDate) {
                setError("La date de naissance est obligatoire pour votre profil astral.");
                return;
            }
            if (!validateEmail(email)) {
                setError("Adresse e-mail invalide.");
                return;
            }
            if (!validatePhone(phone)) {
                setError("Numéro invalide. Uniquement numéros français (01-05, 06, 07, 09).");
                return;
            }
            if (!city.trim() || city.length < 2) {
                setError("Veuillez indiquer votre ville.");
                return;
            }
            if (password.length < 4) {
                setError("Le mot de passe doit contenir au moins 4 caractères.");
                return;
            }
            if (!acceptTerms) {
                setError("Veuillez accepter les Conditions Générales et l'autorisation de contact téléphonique.");
                return;
            }

            const success = registerClient(name, email, phone, city, gender, password, birthDate);
            if (success) {
                closeAuthModal();
            } else {
                setError("Cet email est déjà utilisé par un autre compte.");
            }
        }
    };

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-0 sm:p-4 md:p-6 animate-fade-in">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={closeAuthModal}></div>
            
            {/* Card Container */}
            <div className="relative bg-gradient-to-b from-mystic-900 via-gray-950 to-black w-full h-full sm:h-auto max-h-[100dvh] sm:max-h-[92vh] sm:max-w-md rounded-none sm:rounded-2xl border-0 sm:border border-gold-500/40 shadow-[0_0_60px_rgba(251,191,36,0.18)] overflow-y-auto custom-scrollbar animate-fade-in-up flex flex-col justify-between">
                
                {/* Close Button */}
                <button 
                    onClick={closeAuthModal} 
                    className="absolute top-4 right-4 text-gray-400 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors z-20"
                    title="Fermer"
                >
                    <XIcon className="w-5 h-5" />
                </button>

                {/* Top Banner Header */}
                <div className="bg-gradient-to-r from-gray-900 via-mystic-900 to-black p-6 sm:p-8 text-center border-b border-gold-500/20 relative">
                    <div className="flex justify-center mb-3">
                        <Logo size="sm" showTagline={false} />
                    </div>
                    <h2 className="text-2xl font-serif font-bold text-white tracking-wide">
                        {mode === 'login' ? 'Connexion Espace Client' : 'Création de Compte Privé'}
                    </h2>
                    <p className="text-gray-300 text-xs mt-1 max-w-xs mx-auto">
                        {authPrefill 
                            ? "Sauvegardez vos consultations et retrouvez votre historique en toute sécurité." 
                            : (mode === 'login' ? "Connectez-vous pour accéder à vos consultations." : "Rejoignez AstroX pour échanger avec vos voyants préférés.")}
                    </p>
                </div>

                {/* Form Content */}
                <div className="p-6 sm:p-8">
                    
                    {/* Mode Selector Tabs */}
                    <div className="flex bg-black/60 p-1.5 rounded-xl border border-gold-500/30 mb-6 shadow-inner">
                        <button
                            type="button"
                            onClick={() => { setMode('login'); setError(''); }}
                            className={`flex-1 py-2.5 px-3 text-xs sm:text-sm font-bold rounded-lg transition-all flex items-center justify-center gap-2 ${
                                mode === 'login'
                                    ? 'bg-gradient-to-r from-gold-500 via-amber-400 to-yellow-500 text-gray-950 shadow-md ring-2 ring-gold-400/40'
                                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                            }`}
                        >
                            <span>Se Connecter</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => { setMode('register'); setError(''); }}
                            className={`flex-1 py-2.5 px-3 text-xs sm:text-sm font-bold rounded-lg transition-all flex items-center justify-center gap-2 ${
                                mode === 'register'
                                    ? 'bg-gradient-to-r from-gold-500 via-amber-400 to-yellow-500 text-gray-950 shadow-md ring-2 ring-gold-400/40'
                                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                            }`}
                        >
                            <span>Créer un compte</span>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        
                        {mode === 'register' && (
                            <>
                                {/* Row: Prénom & Civilité */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-1">
                                            Prénom <span className="text-gold-400">*</span>
                                        </label>
                                        <input 
                                            type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            className="w-full bg-black/60 border border-mystic-700/80 focus:border-gold-500 rounded-xl px-3.5 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500/20 transition-all"
                                            placeholder="Ex: Sophie"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-1">
                                            Civilité <span className="text-gold-400">*</span>
                                        </label>
                                        <div className="flex bg-black/60 border border-mystic-700/80 rounded-xl p-1 gap-1">
                                            <button 
                                                type="button" 
                                                onClick={() => setGender('femme')} 
                                                className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${gender === 'femme' ? 'bg-gradient-to-r from-pink-600 to-rose-700 text-white shadow-md' : 'text-gray-400 hover:text-white'}`}
                                            >
                                                Femme
                                            </button>
                                            <button 
                                                type="button" 
                                                onClick={() => setGender('homme')} 
                                                className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${gender === 'homme' ? 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-md' : 'text-gray-400 hover:text-white'}`}
                                            >
                                                Homme
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Date de Naissance */}
                                <div>
                                    <label className="block text-xs font-semibold text-gold-400 uppercase tracking-wider mb-1 flex items-center justify-between">
                                        <span className="flex items-center gap-1">
                                            <span>Date de Naissance</span>
                                            <span className="text-red-400">*</span>
                                        </span>
                                        <span className="text-[10px] text-gray-400 font-normal">Calcul astral & âge</span>
                                    </label>
                                    <input 
                                        type="date"
                                        value={birthDate}
                                        onChange={(e) => setBirthDate(e.target.value)}
                                        className="w-full bg-black/60 border border-gold-500/40 focus:border-gold-400 rounded-xl px-3.5 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-gold-500/30 transition-all color-scheme-dark"
                                        required
                                    />
                                </div>

                                {/* Téléphone & Ville */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-1">
                                            Téléphone <span className="text-gold-400">*</span>
                                        </label>
                                        <input 
                                            type="tel"
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            className="w-full bg-black/60 border border-mystic-700/80 focus:border-gold-500 rounded-xl px-3.5 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500/20 transition-all"
                                            placeholder="06 12 34 56 78"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-1">
                                            Ville / Cité <span className="text-gold-400">*</span>
                                        </label>
                                        <input 
                                            type="text"
                                            value={city}
                                            onChange={(e) => setCity(e.target.value)}
                                            className="w-full bg-black/60 border border-mystic-700/80 focus:border-gold-500 rounded-xl px-3.5 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500/20 transition-all"
                                            placeholder="Ex: Marseille"
                                            required
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Email Field */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-1">
                                Email <span className="text-gold-400">*</span>
                            </label>
                            <input 
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-black/60 border border-mystic-700/80 focus:border-gold-500 rounded-xl px-3.5 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500/20 transition-all"
                                placeholder="votre.email@exemple.com"
                                required
                            />
                        </div>

                        {/* Password Field */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-1">
                                Mot de passe <span className="text-gold-400">*</span>
                            </label>
                            <input 
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full bg-black/60 border border-mystic-700/80 focus:border-gold-500 rounded-xl px-3.5 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500/20 transition-all"
                                placeholder="••••••••"
                                required
                            />
                        </div>

                        {/* Checkbox Conditions Générales & Autorisation Appel Téléphonique (Mode Inscription) */}
                        {mode === 'register' && (
                            <div className="pt-2 pb-1">
                                <label className="flex items-start gap-2.5 cursor-pointer select-none group bg-black/40 p-3 rounded-xl border border-gray-800 hover:border-gold-500/40 transition-colors">
                                    <input 
                                        type="checkbox"
                                        checked={acceptTerms}
                                        onChange={(e) => setAcceptTerms(e.target.checked)}
                                        className="mt-0.5 w-4 h-4 rounded border-gray-600 text-amber-500 focus:ring-amber-400 bg-black/80 shrink-0 cursor-pointer accent-amber-500"
                                        required
                                    />
                                    <span className="text-[11px] text-gray-300 leading-snug">
                                        J'accepte les <button type="button" onClick={() => window.dispatchEvent(new CustomEvent('astro_open_legal'))} className="text-gold-400 font-bold underline hover:text-gold-300">Conditions Générales (CGU)</button> et la Politique de Confidentialité. En renseignant mon numéro, <strong>j'autorise expressément AstroX à me contacter par téléphone et SMS</strong> pour mes consultations conformément aux lois françaises.
                                    </span>
                                </label>
                            </div>
                        )}

                        {/* Error box */}
                        {error && (
                            <div className="bg-red-950/60 border border-red-500/40 text-red-300 text-xs text-center font-semibold p-2.5 rounded-xl animate-shake">
                                {error}
                            </div>
                        )}

                        {/* Submit Button */}
                        <button 
                            type="submit"
                            className="w-full mt-2 bg-gradient-to-r from-gold-500 via-amber-400 to-yellow-500 hover:from-gold-400 hover:to-yellow-300 text-gray-950 font-bold py-3.5 rounded-xl shadow-lg shadow-gold-500/20 transition-all transform active:scale-[0.99] flex items-center justify-center gap-2 text-base"
                        >
                            {mode === 'login' ? 'Se Connecter' : 'Valider Mon Inscription'}
                        </button>
                    </form>

                    {/* Mode Toggle Button */}
                    <div className="mt-6 text-center pt-5 border-t border-white/10">
                        <p className="text-xs text-gray-400 mb-2.5">
                            {mode === 'login' ? "Vous n'avez pas encore de compte client ?" : "Vous possédez déjà un compte client ?"}
                        </p>
                        <button 
                            type="button"
                            onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
                            className="w-full py-2.5 px-4 rounded-xl border border-gold-500/50 hover:border-gold-400 bg-gold-500/10 hover:bg-gold-500/20 text-gold-300 hover:text-gold-200 text-xs sm:text-sm font-bold transition-all flex items-center justify-center gap-2 shadow-sm"
                        >
                            {mode === 'login' ? "Créer un compte privé gratuit" : "Déjà un compte ? Se connecter"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientAuthModal;
